import _ from "lodash";
/**--external-- */
import React from "react";
import classNames from "classnames";
import { Tooltip, Checkbox } from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  WarningTriangleOutlined,
  DotsSixVerticalOutlined,
  LeftArrowOutlined,
  RightArrowOutlined,
} from "@toddle-edu/ds-icons";

/**--internal-- */
import { useI18n } from "Hooks";

/**--relative-- */
import classes from "./LabelCellAsFirstColumn.scss";
import { warningTooltipOverlayStyle } from "./LabelCellAsFirstColumnStyles.js";

const LabelCellAsFirstColumn = props => {
  const {
    renderChildren,
    isError,
    onAddRow,
    areFiltersActive,
    addIconTooltipText,
    currentEditorConfig,
    node,
    onCheckboxClick,
    dragHandleProps,
    isIndentationAllowed,
    onLeftIndent,
    onRightIndent,
    isAddButtonDisabled,
    isLeftIndentationDisabled,
    isRightIndentationDisabled,
  } = props;

  const { t } = useI18n(["common", "scopeAndSequence", "a11y"]);

  const getErrorText = () => {
    const errorCheckFields = currentEditorConfig.errorCheckFields;
    const errorCheckFieldLabels = currentEditorConfig.errorCheckFieldLabels;
    const missingFieldLabels = [];
    for (const field of errorCheckFields) {
      if (_.isEmpty(node[field])) {
        missingFieldLabels.push(t(errorCheckFieldLabels[field]));
      }
    }
    const errorText = missingFieldLabels.join(", ");
    return errorText;
  };

  const handleLeftIndent = () => {
    if (!isLeftIndentationDisabled) {
      onLeftIndent();
    }
  };

  const handleRightIndent = () => {
    if (!isRightIndentationDisabled) {
      onRightIndent();
    }
  };

  const handleCheckboxClick = () => {
    onCheckboxClick({ prevIsChecked: isChecked });
  };

  const handleAddRow = () => {
    if (!isAddButtonDisabled) {
      onAddRow();
    }
  };

  const isChecked = node.isChecked;

  const addIconClasses = classNames(classes.addIcon, {
    [classes.disabledIcon]: isAddButtonDisabled,
  });

  const leftArrowContainerClasses = classNames({
    [classes.leftArrowContainer]: true,
    [classes.disabledIcon]: isLeftIndentationDisabled,
  });

  const rightArrowContainerClasses = classNames({
    [classes.rightArrowContainer]: true,
    [classes.disabledIcon]: isRightIndentationDisabled,
  });

  const leftArrowIconClasses = classNames({
    [classes.disabledIcon]: isLeftIndentationDisabled,
  });

  const rightArrowIconClasses = classNames({
    [classes.disabledIcon]: isRightIndentationDisabled,
  });

  const dragDropContainerClasses = classNames({
    [classes.dragDropIcon]: true,
    [classes.dragDropIconContainer]: !areFiltersActive,
    [classes.dragDropIconContainerDisabled]: areFiltersActive,
    [classes.disabledIcon]: areFiltersActive,
  });

  const dragAndDropIconClasses = classNames({
    [classes.disabledIcon]: areFiltersActive,
  });

  return (
    <div className={classes.container} tabIndex="0">
      <div className={classes.actionButtonContainer}>
        {!isError ? (
          <div className={classes.addIconContainer}>
            <button
              className={addIconClasses}
              onClick={handleAddRow}
              aria-label={t("a11y:add_row")}
            >
              <Tooltip tooltip={t(addIconTooltipText)} placement={"top"}>
                <AddCircleOutlined
                  className={addIconClasses}
                  size={"xx-small"}
                />
              </Tooltip>
            </button>
          </div>
        ) : (
          <div className={classes.warningIconContainer}>
            <div className={classes.warningIcon}>
              <Tooltip
                tooltip={getErrorText()}
                placement="topLeft"
                overlayStyle={warningTooltipOverlayStyle}
              >
                <WarningTriangleOutlined
                  width={16}
                  height={16}
                  variant="critical"
                />
              </Tooltip>
            </div>
          </div>
        )}
        <div className={classes.checkboxContainer}>
          <Checkbox
            isChecked={isChecked}
            onChange={handleCheckboxClick}
            size="xx-small"
          />
        </div>
        {isIndentationAllowed ? (
          <div className={classes.arrowsContainer}>
            <button
              className={leftArrowContainerClasses}
              onClick={handleLeftIndent}
              aria-label={t("a11y:indent_left")}
            >
              <LeftArrowOutlined
                className={leftArrowIconClasses}
                size={"xxx-small"}
              />
            </button>
            <button
              className={rightArrowContainerClasses}
              onClick={handleRightIndent}
              aria-label={t("a11y:indent_right")}
            >
              <RightArrowOutlined
                className={rightArrowIconClasses}
                size={"xxx-small"}
              />
            </button>
          </div>
        ) : null}
        <div {...dragHandleProps} className={dragDropContainerClasses}>
          <DotsSixVerticalOutlined
            className={dragAndDropIconClasses}
            size={"x-small"}
          />
        </div>
      </div>
      {renderChildren ? renderChildren() : null}
    </div>
  );
};

export default LabelCellAsFirstColumn;
